// Zoe Sleepy Trouble Bubble — dreamy star-bubble floats to target, erupts in sparkles.
// Phases: FLOAT (bubble travels) → IMPACT (star burst + ZZZ).

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const FLOAT_MS  = 420;
const IMPACT_MS = 380;
const TOTAL     = FLOAT_MS + IMPACT_MS;

const BUBBLE_R  = 11;
const STAR_ARMS = 6;

// Sparkle ring positions (angle offsets)
const SPARKLE_COUNT = 12;

export class ZoeBubbleVFX implements Animation {
  private elapsed = 0;
  private angle: number;

  // Pre-baked sparkle directions so they're consistent per instance
  private sparkleAngles: number[];

  constructor(
    private from: Point,
    private to: Point,
    private onImpact?: () => void,
  ) {
    this.angle = Math.atan2(to.y - from.y, to.x - from.x);
    this.sparkleAngles = Array.from({ length: SPARKLE_COUNT }, (_, i) =>
      (i / SPARKLE_COUNT) * Math.PI * 2 + Math.sin(i * 1.3) * 0.4,
    );
  }

  update(dt: number): void {
    const prev = this.elapsed;
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);
    if (prev < FLOAT_MS && this.elapsed >= FLOAT_MS) this.onImpact?.();
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.elapsed < FLOAT_MS) {
      this.drawFloat(ctx, Easing.easeOutQuad(this.elapsed / FLOAT_MS));
    } else {
      this.drawImpact(ctx, (this.elapsed - FLOAT_MS) / IMPACT_MS);
    }
  }

  private drawFloat(ctx: CanvasRenderingContext2D, t: number): void {
    const x = this.from.x + (this.to.x - this.from.x) * t;
    // Slight arc upward: sine wave dip
    const y = this.from.y + (this.to.y - this.from.y) * t - Math.sin(t * Math.PI) * 22;

    ctx.save();

    // Dreamy glow trail
    const trailT = Math.max(0, t - 0.12);
    const tx = this.from.x + (this.to.x - this.from.x) * trailT;
    const ty = this.from.y + (this.to.y - this.from.y) * trailT - Math.sin(trailT * Math.PI) * 22;
    const grad = ctx.createLinearGradient(tx, ty, x, y);
    grad.addColorStop(0, "rgba(220,140,255,0)");
    grad.addColorStop(1, "rgba(255,180,255,0.35)");
    ctx.strokeStyle = grad;
    ctx.lineWidth = BUBBLE_R * 1.4;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(x, y);
    ctx.stroke();

    // Rotating star sparkles orbiting the bubble
    const rotation = this.elapsed * 0.004;
    for (let i = 0; i < 5; i++) {
      const a = rotation + (i / 5) * Math.PI * 2;
      const orbitR = BUBBLE_R + 7;
      const sx = x + Math.cos(a) * orbitR;
      const sy = y + Math.sin(a) * orbitR;
      ctx.globalAlpha = 0.7;
      ctx.fillStyle = i % 2 === 0 ? "#ffddff" : "#cc88ff";
      ctx.shadowColor = "#ff99ff";
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(sx, sy, 2 + (i % 2), 0, Math.PI * 2);
      ctx.fill();
    }

    // Bubble body — translucent gradient sphere
    ctx.globalAlpha = 0.88;
    ctx.shadowColor = "#dd88ff";
    ctx.shadowBlur = 20;
    const bubbleGrad = ctx.createRadialGradient(x - 3, y - 3, 1, x, y, BUBBLE_R);
    bubbleGrad.addColorStop(0, "#ffffff");
    bubbleGrad.addColorStop(0.3, "#ffccff");
    bubbleGrad.addColorStop(0.7, "#cc66ff");
    bubbleGrad.addColorStop(1, "#8833cc");
    ctx.fillStyle = bubbleGrad;
    ctx.beginPath();
    ctx.arc(x, y, BUBBLE_R, 0, Math.PI * 2);
    ctx.fill();

    // Star shape overlay
    ctx.globalAlpha = 0.4;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1;
    ctx.shadowBlur = 6;
    ctx.beginPath();
    this.drawStar(ctx, x, y, BUBBLE_R * 0.45, BUBBLE_R * 0.85, STAR_ARMS, rotation * 2);
    ctx.stroke();

    // Highlight glint
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = "#ffffff";
    ctx.shadowBlur = 4;
    ctx.beginPath();
    ctx.arc(x - 3.5, y - 3.5, 2.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private drawImpact(ctx: CanvasRenderingContext2D, t: number): void {
    const x = this.to.x;
    const y = this.to.y;
    const fade = 1 - Easing.easeInQuad(t);

    ctx.save();

    // Expanding ring
    ctx.globalAlpha = fade * 0.7;
    ctx.strokeStyle = "#ee88ff";
    ctx.lineWidth = 2.5;
    ctx.shadowColor = "#cc55ff";
    ctx.shadowBlur = 18;
    ctx.beginPath();
    ctx.arc(x, y, BUBBLE_R + t * 32, 0, Math.PI * 2);
    ctx.stroke();

    // Secondary ring (star-bubble pop)
    ctx.globalAlpha = fade * 0.4;
    ctx.strokeStyle = "#ffccff";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(x, y, BUBBLE_R + t * 18, 0, Math.PI * 2);
    ctx.stroke();

    // Sparkle rays
    ctx.globalAlpha = fade * 0.85;
    ctx.strokeStyle = "#ffddff";
    ctx.lineWidth = 1.5;
    ctx.shadowColor = "#dd88ff";
    ctx.shadowBlur = 10;
    for (const a of this.sparkleAngles) {
      const len = 8 + t * 22;
      const inner = 4 + t * 6;
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(a) * inner, y + Math.sin(a) * inner);
      ctx.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len);
      ctx.stroke();
    }

    // ZZZ text rising
    if (t < 0.7) {
      const textFade = t < 0.5 ? 1 : 1 - (t - 0.5) / 0.2;
      ctx.globalAlpha = textFade * 0.9;
      ctx.fillStyle = "#ffccff";
      ctx.shadowColor = "#aa44cc";
      ctx.shadowBlur = 12;
      ctx.font = `bold ${14 + t * 4}px "Inter", sans-serif`;
      ctx.textAlign = "center";
      ctx.fillText("ZZZ", x, y - 28 - t * 20);
    }

    ctx.restore();
  }

  private drawStar(
    ctx: CanvasRenderingContext2D,
    cx: number, cy: number,
    inner: number, outer: number,
    points: number,
    rotation: number,
  ): void {
    for (let i = 0; i < points * 2; i++) {
      const a = rotation + (i / (points * 2)) * Math.PI * 2;
      const r = i % 2 === 0 ? outer : inner;
      if (i === 0) ctx.moveTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
      else ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
    }
    ctx.closePath();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
